"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ConversationStreamingServiceInstance = void 0;
const client_1 = require("@rws-framework/client");
/**
 * Real-time audio streaming service for conversation agents
 * Based on the GitHub example implementation
 */
class ConversationStreamingService extends client_1.RWSService {
    constructor() {
        super(...arguments);
        this.audioContext = null;
        this.mediaStreamSource = null;
        this.processor = null;
        this.stream = null;
        this.isStreaming = false;
        // Audio streaming settings optimized for ElevenLabs
        this.SAMPLE_RATE = 16000;
        this.CHANNELS = 1;
        this.BUFFER_SIZE = 2048; // Smaller buffer for lower latency
        this.CHUNK_DURATION = 100; // Send chunks every 100ms
        this.audioBuffer = [];
        this.lastSendTime = 0;
        this.onAudioChunk = null;
        this.onVADScore = null;
    }
    /**
     * Initialize audio streaming
     */
    async initializeStreaming(options = {}) {
        try {
            if (!navigator.mediaDevices) {
                console.error('❌ [StreamingService] getUserMedia not supported!');
                throw new Error('getUserMedia not supported!');
            }
            this.stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    sampleRate: options.sampleRate || this.SAMPLE_RATE,
                    channelCount: options.channels || this.CHANNELS,
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true
                }
            });
            // Create AudioContext without forcing sample rate to avoid Firefox mismatch
            // Let it use the default sample rate that matches the MediaStream
            this.audioContext = new AudioContext();
            this.mediaStreamSource = this.audioContext.createMediaStreamSource(this.stream);
            console.log(`🎤 [StreamingService] Audio initialized - Context: ${this.audioContext.sampleRate}Hz, Stream tracks:`, this.stream.getAudioTracks().map(track => ({
                label: track.label,
                settings: track.getSettings()
            })));
            // Use ScriptProcessor for compatibility (AudioWorklet is more modern but needs more setup)
            this.processor = this.audioContext.createScriptProcessor(options.bufferSize || this.BUFFER_SIZE, options.channels || this.CHANNELS, options.channels || this.CHANNELS);
            return true;
        }
        catch (error) {
            console.error('💥 [StreamingService] Failed to initialize audio streaming:', error);
            return false;
        }
    }
    /**
     * Start real-time audio streaming
     */
    async startStreaming(onAudioChunk, onVADScore) {
        try {
            if (this.isStreaming) {
                console.log('⚠️ [StreamingService] Already streaming, returning true');
                return true;
            }
            if (!this.processor || !this.mediaStreamSource || !this.audioContext) {
                console.error('❌ [StreamingService] Audio streaming not initialized');
                throw new Error('Audio streaming not initialized');
            }
            this.onAudioChunk = onAudioChunk;
            this.onVADScore = onVADScore;
            this.audioBuffer = [];
            this.lastSendTime = 0;
            // Set up audio processing
            this.processor.onaudioprocess = (e) => {
                var _a, _b;
                if (!this.isStreaming)
                    return;
                const inputData = e.inputBuffer.getChannelData(0);
                const currentTime = Date.now();
                // Calculate VAD score for display
                const vadScore = this.calculateVADScore(inputData);
                (_a = this.onVADScore) === null || _a === void 0 ? void 0 : _a.call(this, vadScore);
                // Resample to 16kHz if AudioContext is using a higher sample rate
                let processedData = inputData;
                if (this.audioContext && this.audioContext.sampleRate !== this.SAMPLE_RATE) {
                    processedData = this.resampleAudio(inputData, this.audioContext.sampleRate, this.SAMPLE_RATE);
                }
                // Convert to PCM with quality optimization
                const pcmData = new Int16Array(processedData.length);
                for (let i = 0; i < processedData.length; i++) {
                    // Apply gentle compression to reduce clipping
                    const sample = Math.max(-0.95, Math.min(0.95, processedData[i]));
                    pcmData[i] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
                }
                // Convert to Uint8Array
                const uint8Chunk = this.int16ToUint8(pcmData);
                // Accumulate audio
                this.audioBuffer.push(uint8Chunk);
                // Send at regular intervals to prevent audio fragmentation
                if (currentTime - this.lastSendTime >= this.CHUNK_DURATION && this.audioBuffer.length > 0) {
                    const combinedChunk = this.concatenateUint8Arrays(this.audioBuffer);
                    this.audioBuffer = []; // Reset buffer
                    // Send with base64 encoding
                    const base64 = btoa(String.fromCharCode(...combinedChunk));
                    (_b = this.onAudioChunk) === null || _b === void 0 ? void 0 : _b.call(this, base64);
                    this.lastSendTime = currentTime;
                }
            };
            // Connect audio nodes
            this.mediaStreamSource.connect(this.processor);
            this.processor.connect(this.audioContext.destination);
            this.isStreaming = true;
            return true;
        }
        catch (error) {
            console.error('💥 [StreamingService] Failed to start audio streaming:', error);
            return false;
        }
    }
    /**
     * Stop audio streaming
     */
    stopStreaming() {
        if (!this.isStreaming)
            return;
        this.isStreaming = false;
        // Disconnect audio nodes
        if (this.processor) {
            this.processor.disconnect();
            this.processor.onaudioprocess = null;
        }
        if (this.mediaStreamSource) {
            this.mediaStreamSource.disconnect();
        }
        if (this.audioContext) {
            this.audioContext.close();
            this.audioContext = null;
        }
        // Stop media stream
        if (this.stream) {
            this.stream.getTracks().forEach(track => track.stop());
            this.stream = null;
        }
        // Reset state
        this.mediaStreamSource = null;
        this.processor = null;
        this.audioBuffer = [];
        this.onAudioChunk = null;
        this.onVADScore = null;
    }
    /**
     * Check if currently streaming
     */
    getIsStreaming() {
        return this.isStreaming;
    }
    /**
     * Calculate VAD (Voice Activity Detection) score
     */
    calculateVADScore(audioData) {
        let sum = 0;
        let maxAmplitude = 0;
        for (let i = 0; i < audioData.length; i++) {
            const absValue = Math.abs(audioData[i]);
            sum += absValue;
            maxAmplitude = Math.max(maxAmplitude, absValue);
        }
        const avgAmplitude = sum / audioData.length;
        // Log quality issues
        if (maxAmplitude > 0.9) {
            console.warn('⚠️ Audio clipping detected, reducing volume');
        }
        return avgAmplitude;
    }
    /**
     * Convert Int16Array to Uint8Array for base64 encoding
     */
    int16ToUint8(int16Array) {
        const uint8Array = new Uint8Array(int16Array.buffer);
        return uint8Array;
    }
    /**
     * Helper function to concatenate Uint8Arrays
     */
    concatenateUint8Arrays(arrays) {
        const totalLength = arrays.reduce((acc, arr) => acc + arr.length, 0);
        const result = new Uint8Array(totalLength);
        let offset = 0;
        for (const arr of arrays) {
            result.set(arr, offset);
            offset += arr.length;
        }
        return result;
    }
    /**
     * Resample audio from source sample rate to target sample rate
     */
    resampleAudio(inputBuffer, sourceSampleRate, targetSampleRate) {
        if (sourceSampleRate === targetSampleRate) {
            return inputBuffer;
        }
        const ratio = sourceSampleRate / targetSampleRate;
        const outputLength = Math.ceil(inputBuffer.length / ratio);
        const outputBuffer = new Float32Array(outputLength);
        for (let i = 0; i < outputLength; i++) {
            const sourceIndex = i * ratio;
            const index = Math.floor(sourceIndex);
            const fraction = sourceIndex - index;
            if (index + 1 < inputBuffer.length) {
                // Linear interpolation for better quality
                outputBuffer[i] = inputBuffer[index] * (1 - fraction) + inputBuffer[index + 1] * fraction;
            }
            else {
                outputBuffer[i] = inputBuffer[index] || 0;
            }
        }
        return outputBuffer;
    }
    /**
     * Clean up all resources
     */
    destroy() {
        this.stopStreaming();
    }
}
exports.ConversationStreamingServiceInstance = ConversationStreamingService;
exports.default = ConversationStreamingService.getSingleton();
